/*
  <product-tabs>: tab chips that switch between server-rendered carousel panels.

  Markup inside the element:
    [role="tab"][data-tab="<n>"][aria-controls="<panel id>"]   tab chips
    [role="tabpanel"] > scroll-carousel                        one panel per tab
    [data-helper-tab="<n>"]                                     helper buttons: open tab n, pressed again → first tab
    [data-tabs-nav] > [data-tabs-prev] / [data-tabs-next]       arrows for the visible carousel
  In the theme editor, selecting a tab block shows its panel.
*/
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        if (!this.tabs.length) return;
        this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
        this.helpers = [...this.querySelectorAll('[data-helper-tab]')];
        this.list = this.querySelector('[role="tablist"]');
        this.nav = this.querySelector('[data-tabs-nav]');
        this.prev = this.querySelector('[data-tabs-prev]');
        this.next = this.querySelector('[data-tabs-next]');

        const LM = window.LM || {};
        const debounce =
          LM.debounce ||
          ((fn, wait) => {
            let timer;
            return () => {
              window.clearTimeout(timer);
              timer = window.setTimeout(fn, wait);
            };
          });
        this.syncNav = debounce(() => this.updateNav(), 80);

        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
          if (index > -1) this.select(index);
        });
        this.panels.forEach((panel) => {
          const carousel = panel && panel.querySelector('scroll-carousel');
          if (carousel) carousel.addEventListener('scroll', this.syncNav, { passive: true });
        });
        window.addEventListener('resize', this.syncNav);

        const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this.select(Math.max(0, current), { reset: false });
      }

      disconnectedCallback() {
        if (this.syncNav) window.removeEventListener('resize', this.syncNav);
      }

      get carousel() {
        const panel = this.panels[this.index];
        return panel ? panel.querySelector('scroll-carousel') : null;
      }

      onClick(event) {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.contains(tab)) return this.select(this.tabs.indexOf(tab));

        const helper = event.target.closest('[data-helper-tab]');
        if (helper) {
          const index = this.tabs.findIndex((item) => item.dataset.tab === helper.dataset.helperTab);
          if (index > -1) this.select(index === this.index ? 0 : index);
          return;
        }

        const arrow = event.target.closest('[data-tabs-prev], [data-tabs-next]');
        const carousel = this.carousel;
        if (!arrow || !carousel) return;
        const direction = arrow.hasAttribute('data-tabs-next') ? 1 : -1;
        if (carousel.step) return carousel.step(direction);
        carousel.scrollBy({ left: direction * carousel.clientWidth * 0.75, behavior: 'smooth' });
      }

      onKeydown(event) {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;
        const last = this.tabs.length - 1;
        let index = this.tabs.indexOf(tab);
        if (event.key === 'ArrowRight') index = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') index = index === 0 ? last : index - 1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = last;
        else return;
        event.preventDefault();
        this.select(index);
        this.tabs[index].focus();
      }

      select(index, { reset = true } = {}) {
        this.index = index;
        const active = this.tabs[index];
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (this.panels[i]) this.panels[i].hidden = !selected;
        });
        this.helpers.forEach((helper) => {
          helper.setAttribute('aria-pressed', String(helper.dataset.helperTab === active.dataset.tab));
        });

        // Keep the active chip visible when the chip row scrolls (small screens).
        if (this.list && this.list.scrollWidth > this.list.clientWidth) {
          const listBox = this.list.getBoundingClientRect();
          const tabBox = active.getBoundingClientRect();
          if (tabBox.left < listBox.left || tabBox.right > listBox.right) {
            this.list.scrollLeft += tabBox.left - listBox.left - 16;
          }
        }

        const carousel = this.carousel;
        if (carousel && reset) carousel.scrollLeft = 0;
        this.updateNav();
      }

      updateNav() {
        const carousel = this.carousel;
        if (!carousel || !this.nav) return;
        const max = carousel.scrollWidth - carousel.clientWidth - 2;
        if (this.prev) this.prev.disabled = carousel.scrollLeft <= 2;
        if (this.next) this.next.disabled = carousel.scrollLeft >= max;
        this.nav.hidden = max <= 2;
      }
    }
  );
}
